import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { useEffect } from "react";

export interface BrandingConfig {
  logoUrl: string | null;
  brandName: string;
  primaryColorHsl: string;
  primaryFgColorHsl: string;
  accentColorHsl: string;
  accentFgColorHsl: string;
  sidebarColorHsl: string | null;
  borderRadiusRem: string;
  headingFont: string;
  faviconUrl: string | null;
}

function applyBranding(branding: BrandingConfig) {
  const root = document.documentElement;

  root.style.setProperty("--primary", `hsl(${branding.primaryColorHsl})`);
  root.style.setProperty(
    "--primary-foreground",
    `hsl(${branding.primaryFgColorHsl})`,
  );
  root.style.setProperty(
    "--accent-foreground",
    `hsl(${branding.accentFgColorHsl})`,
  );
  root.style.setProperty("--ring", `hsl(${branding.primaryColorHsl})`);
  root.style.setProperty("--radius", `${branding.borderRadiusRem}rem`);
  root.style.setProperty("--chart-1", `hsl(${branding.primaryColorHsl})`);

  // Sidebar primary matches main primary unless overridden
  const sidebarColor = branding.sidebarColorHsl || branding.primaryColorHsl;
  root.style.setProperty("--sidebar-primary", `hsl(${sidebarColor})`);
  root.style.setProperty("--sidebar-ring", `hsl(${sidebarColor})`);
  root.style.setProperty(
    "--sidebar-accent-foreground",
    `hsl(${sidebarColor})`,
  );

  // Heading font
  root.style.setProperty(
    "--font-heading",
    `"${branding.headingFont}", "Inter", -apple-system, sans-serif`,
  );

  // Favicon
  if (branding.faviconUrl) {
    const link = document.querySelector(
      "link[rel='icon']",
    ) as HTMLLinkElement | null;
    if (link) link.href = branding.faviconUrl;
  }

  // Page title
  document.title = `${branding.brandName} - Hosting Management System`;
}

export function useBranding() {
  const { data: branding, isLoading } = useQuery<BrandingConfig>({
    queryKey: ["/api/billing/settings/branding"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (branding) applyBranding(branding);
  }, [branding]);

  return {
    branding: branding ?? null,
    isLoading,
    logoUrl: branding?.logoUrl ?? null,
    brandName: branding?.brandName ?? "HeyHiHello",
  };
}
